import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDrives, useDocuments, useAgent } from '../../../context';
import { useStore } from '../../../../../../hooks';
import { unassignAgentFromDocument, deleteDocument } from '../../../../../../services/document';
import { unassignAgentFromVault } from '../../../../../../services/vault';
import useDebounce from '../../../../../../hooks/useDebounce';

/**
 * Hook para manejar la lógica de KnowledgePage
 * 
 * Tab 0 (Drives): Muestra drives + documentos ASIGNADOS al agente
 * Tab 1 (Documents): Muestra documentos del VAULT PROPIO del agente
 */
const useKnowledgePage = () => {
  // Contexto de Drives (drives + documentos asignados al agente)
  const {
    drives,
    agentDocuments,
    loading: drivesLoading,
    error: drivesError,
    hasFetched: drivesFetched,
    fetchAll: fetchDrivesAndAgentDocs,
    drivesCount,
    agentDocumentsCount,
    removeDrive,
    removeAgentDocument,
  } = useDrives();

  // Contexto del Agente
  const { agentId } = useAgent();

  // Store para auth
  const { authStore } = useStore();

  // Contexto de Documents (documentos del vault propio del agente)
  const {
    documents: vaultDocuments,
    loading: vaultDocumentsLoading,
    error: vaultDocumentsError,
    hasFetched: vaultDocumentsFetched,
    fetchDocuments: fetchVaultDocuments,
    documentsCount: vaultDocumentsCount,
    removeDocument: removeVaultDocument,
    vaultUuid,
  } = useDocuments();

  // Estado local de UI
  const [activeTab, setActiveTab] = useState(0); // 0 = Drives, 1 = Documents
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState(''); // Estado para búsqueda
  const [selectedDrive, setSelectedDrive] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDriveDetail, setShowDriveDetail] = useState(false);
  const [showDocumentDetail, setShowDocumentDetail] = useState(false);

  // Debounce del término de búsqueda
  const debouncedSearchTerm = useDebounce(searchTerm, 700);

  // Estado de loading para acciones (IDs de items siendo procesados)
  const [removingDocumentIds, setRemovingDocumentIds] = useState(new Set()); // Tab Drives - Remove access
  const [removingDriveIds, setRemovingDriveIds] = useState(new Set()); // Tab Drives - Remove drive
  const [deletingDocumentIds, setDeletingDocumentIds] = useState(new Set()); // Tab Documents - Delete

  // Estados para modals de confirmación
  const [showRemoveDriveModal, setShowRemoveDriveModal] = useState(false);
  const [showRemoveDocumentModal, setShowRemoveDocumentModal] = useState(false);
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);

  // Fetch inicial
  useEffect(() => {
    if (!drivesFetched) {
      fetchDrivesAndAgentDocs();
    }
  }, [drivesFetched, fetchDrivesAndAgentDocs]);

  // Fetch de documentos del vault cuando cambiamos a esa tab o cuando vaultUuid está disponible
  useEffect(() => {
    if (activeTab === 1 && vaultUuid && !vaultDocumentsFetched) {
      fetchVaultDocuments();
    }
  }, [activeTab, vaultUuid, vaultDocumentsFetched, fetchVaultDocuments]);

  // Estado combinado según tab activo
  const loading = activeTab === 0 ? drivesLoading : vaultDocumentsLoading;
  const error = activeTab === 0 ? drivesError : vaultDocumentsError;

  // Items filtrados según búsqueda
  const filteredDrives = useMemo(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.trim() === '') {
      return drives;
    }
    const searchLower = debouncedSearchTerm.toLowerCase();
    return drives.filter(drive => 
      drive.name?.toLowerCase().includes(searchLower) ||
      drive.vaultUuid?.toLowerCase().includes(searchLower) ||
      drive.uuid?.toLowerCase().includes(searchLower)
    );
  }, [drives, debouncedSearchTerm]);

  const filteredAgentDocuments = useMemo(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.trim() === '') {
      return agentDocuments;
    }
    const searchLower = debouncedSearchTerm.toLowerCase();
    return agentDocuments.filter(doc =>
      doc.name?.toLowerCase().includes(searchLower) ||
      doc.type?.toLowerCase().includes(searchLower) ||
      doc.uuid?.toLowerCase().includes(searchLower) ||
      doc.metadata?.source?.toLowerCase().includes(searchLower)
    );
  }, [agentDocuments, debouncedSearchTerm]);

  const filteredVaultDocuments = useMemo(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.trim() === '') {
      return vaultDocuments;
    }
    const searchLower = debouncedSearchTerm.toLowerCase();
    return vaultDocuments.filter(doc =>
      doc.name?.toLowerCase().includes(searchLower) ||
      doc.type?.toLowerCase().includes(searchLower) ||
      doc.uuid?.toLowerCase().includes(searchLower) ||
      doc.metadata?.source?.toLowerCase().includes(searchLower)
    );
  }, [vaultDocuments, debouncedSearchTerm]);

  // Items actuales según tab activo (ahora filtrados)
  const currentItems = useMemo(() => {
    if (activeTab === 0) {
      // Tab Drives: combinamos drives + documentos asignados al agente (filtrados)
      return [...filteredDrives, ...filteredAgentDocuments];
    }
    // Tab Documents: documentos del vault propio (filtrados)
    return filteredVaultDocuments;
  }, [activeTab, filteredDrives, filteredAgentDocuments, filteredVaultDocuments]);

  // Contadores actualizados con items filtrados
  const totalDrivesTabItems = filteredDrives.length + filteredAgentDocuments.length;
  const totalDocumentsTabItems = filteredVaultDocuments.length;
  const totalItems = activeTab === 0 ? totalDrivesTabItems : totalDocumentsTabItems;

  // Resetear búsqueda al cambiar de tab
  useEffect(() => {
    setSearchTerm('');
  }, [activeTab]);

  // Handlers
  const handleTabChange = useCallback((index) => {
    setActiveTab(index);
  }, []);

  // Handler para cambio de búsqueda
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const handleAddClick = useCallback((type) => {
    // TODO: Implementar slideout para agregar drives/documentos
    console.log('Add clicked:', type);
  }, []);

  // Drive handlers
  const handleDriveClick = useCallback((drive) => {
    setSelectedDrive(drive);
    setShowDriveDetail(true);
  }, []);

  const handleRemoveDriveAccess = useCallback((drive) => {
    setItemToRemove(drive);
    setShowRemoveDriveModal(true);
  }, []);

  const handleConfirmRemoveDrive = useCallback(async () => {
    if (!itemToRemove || !agentId) return;
    
    const driveId = itemToRemove.vaultUuid || itemToRemove.uuid || itemToRemove.id;
    setRemovingDriveIds((prev) => new Set([...prev, driveId]));
    
    try {
      await unassignAgentFromVault(driveId, agentId);
      console.log('Drive access removed successfully:', driveId);
      removeDrive(driveId);
    } catch (error) {
      console.error('Error removing drive access:', error);
      // TODO: Mostrar notificación de error
    } finally {
      setRemovingDriveIds((prev) => {
        const next = new Set(prev);
        next.delete(driveId);
        return next;
      });
      setShowRemoveDriveModal(false);
      setItemToRemove(null);
    }
  }, [itemToRemove, agentId, removeDrive]);

  const handleCloseDriveDetail = useCallback(() => {
    setShowDriveDetail(false);
    setSelectedDrive(null);
  }, []);

  // Document handlers (para documentos asignados al agente - Tab Drives)
  const handleAgentDocumentClick = useCallback((document) => {
    setSelectedDocument(document);
    setShowDocumentDetail(true);
  }, []);

  const handleDeleteAgentDocument = useCallback((document) => {
    setItemToRemove(document);
    setShowRemoveDocumentModal(true);
  }, []);

  const handleConfirmRemoveDocument = useCallback(async () => {
    if (!itemToRemove) return;
    
    const documentId = itemToRemove.uuid || itemToRemove.id;
    
    // Marcar como "removiendo" - mostrar loading en la card
    setRemovingDocumentIds((prev) => new Set([...prev, documentId]));
    
    try {
      await unassignAgentFromDocument(documentId, agentId);
      console.log('Document access removed successfully:', documentId);
      // Éxito: eliminar de la lista
      removeAgentDocument(documentId);
    } catch (error) {
      console.error('Error removing document access:', error);
      // TODO: Mostrar notificación de error
    } finally {
      // Quitar del estado de "removiendo"
      setRemovingDocumentIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
      setShowRemoveDocumentModal(false);
      setItemToRemove(null);
    }
  }, [itemToRemove, agentId, authStore, removeAgentDocument]);

  // Document handlers (para documentos del vault - Tab Documents)
  const handleVaultDocumentClick = useCallback((document) => {
    setSelectedDocument(document);
    setShowDocumentDetail(true);
  }, []);

  const handleDeleteVaultDocument = useCallback((document) => {
    setItemToRemove(document);
    setShowDeleteDocumentModal(true);
  }, []);

  const handleConfirmDeleteDocument = useCallback(async () => {
    if (!itemToRemove) return;
    
    const documentId = itemToRemove.uuid || itemToRemove.id;
    const tenantId = authStore?.selectedAccount?.id || '58145bef-4f71-468a-bded-1823d2a08f58';
    
    // Marcar como "eliminando" - mostrar loading en la card
    setDeletingDocumentIds((prev) => new Set([...prev, documentId]));
    
    try {
      // Paso 1: Desasignar el documento del agente
      await unassignAgentFromDocument(documentId, agentId, tenantId);
      console.log('Document unassigned from agent:', documentId);
      
      // Paso 2: Eliminar el documento del vault
      await deleteDocument({ document_uuid: documentId });
      console.log('Document deleted successfully:', documentId);
      
      // Éxito: eliminar de la lista
      removeVaultDocument(documentId);
    } catch (error) {
      console.error('Error deleting vault document:', error);
      // TODO: Mostrar notificación de error
    } finally {
      // Quitar del estado de "eliminando"
      setDeletingDocumentIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
      setShowDeleteDocumentModal(false);
      setItemToRemove(null);
    }
  }, [itemToRemove, agentId, authStore, removeVaultDocument]);

  const handleCloseDocumentDetail = useCallback(() => {
    setShowDocumentDetail(false);
    setSelectedDocument(null);
  }, []);

  // Helper para determinar el tipo de item
  const isDrive = useCallback((item) => {
    return !!item.vaultUuid || !!item.vault_uuid;
  }, []);

  // Handlers para cerrar modals
  const handleCloseRemoveDriveModal = useCallback(() => {
    setShowRemoveDriveModal(false);
    setItemToRemove(null);
  }, []);

  const handleCloseRemoveDocumentModal = useCallback(() => {
    setShowRemoveDocumentModal(false);
    setItemToRemove(null);
  }, []);

  const handleCloseDeleteDocumentModal = useCallback(() => {
    setShowDeleteDocumentModal(false);
    setItemToRemove(null);
  }, []);

  return {
    // Estado
    activeTab,
    viewMode,
    searchTerm,
    drives: filteredDrives,
    agentDocuments: filteredAgentDocuments, // Documentos asignados al agente (filtrados)
    vaultDocuments: filteredVaultDocuments, // Documentos del vault propio (filtrados)
    currentItems,
    loading,
    error,
    totalItems,
    totalDrivesTabItems,
    totalDocumentsTabItems,
    drivesCount,
    agentDocumentsCount,
    vaultDocumentsCount,
    vaultUuid,

    // Selección y detail slideouts
    selectedDrive,
    selectedDocument,
    showDriveDetail,
    showDocumentDetail,

    // Handlers de UI
    handleTabChange,
    handleViewModeChange,
    handleSearchChange,
    handleAddClick,

    // Handlers de drives
    handleDriveClick,
    handleRemoveDriveAccess,
    handleCloseDriveDetail,

    // Handlers de agent documents (Tab Drives)
    handleAgentDocumentClick,
    handleDeleteAgentDocument,

    // Handlers de vault documents (Tab Documents)
    handleVaultDocumentClick,
    handleDeleteVaultDocument,
    handleCloseDocumentDetail,

    // Helpers
    isDrive,

    // Loading states para acciones
    removingDocumentIds, // Tab Drives - Remove access
    removingDriveIds, // Tab Drives - Remove drive
    deletingDocumentIds, // Tab Documents - Delete

    // Modals
    showRemoveDriveModal,
    showRemoveDocumentModal,
    showDeleteDocumentModal,
    itemToRemove,
    handleConfirmRemoveDrive,
    handleConfirmRemoveDocument,
    handleConfirmDeleteDocument,
    handleCloseRemoveDriveModal,
    handleCloseRemoveDocumentModal,
    handleCloseDeleteDocumentModal,
  };
};

export default useKnowledgePage;
